import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useEmailTracking } from '../../hooks/useEmailTracking';
import { mapTimelineStatus } from '../../helper/common.helper';
import {
    DocumentOutcome,
    EmailStatus,
    EmailTrackingKind,
    TimelineStatus,
    TimelineStepType,
} from '../../features/email-tracking/email-tracking.types';
import { TimelineStatusIcon, StepIcon, FailedIcon, CloseIcon, ChevronDownIcon, ChevronUpIcon } from './email-tracking-icons';
import './../../scss/components/email-tracking-modal.style.scss';

export type EmailTrackingVariant = 'invoice' | 'document';

interface EmailTrackingModalProps {
    show: boolean;
    /** Internal numeric id whose tracking to load; null when closed. */
    invoiceId: number | null;
    variant?: EmailTrackingVariant;
    kind?: EmailTrackingKind;
    /** Invoice-progress flags carried off the invoice row (drive the last three nodes). */
    invoiceStatus?: number;
    isSalaryCreated?: boolean;
    invoiceSalaryStatus?: number;
    documentOutcome?: DocumentOutcome;
    documentSentDate?: string | null;
    documentNumber?: string;
    titleKey?: string;
    onClose: () => void;
}

/** invoice.invoiceStatus value that means the invoice was paid (InvoiceStatus.Paid). */
const INVOICE_PAID_STATUS = 2;

/** invoice.invoiceSalaryStatus value that means salary has been paid out. */
const SALARY_PAID_STATUS = 2;

const EMAIL_STEPS: TimelineStepType[] = ['sent', 'delivered', 'opened'];

const EMAIL_STEP_COUNT = EMAIL_STEPS.length;

const STEP_ORDER_BY_VARIANT: Record<EmailTrackingVariant, TimelineStepType[]> = {
    invoice: [...EMAIL_STEPS, 'paid', 'salaryCreated', 'salaryPaid'],
    document: [...EMAIL_STEPS, 'signed'],
};

/** How many email steps have occurred for a given email status. */
const EMAIL_REACHED: Record<EmailStatus, number> = {
    not_sent: 0,
    failed: 0,
    sent: 1,
    bounced: 1,
    delivered: 2,
    opened: 3,
};

const STEP_LABEL_KEY: Record<TimelineStepType, string> = {
    sent: 'EMAIL_SENT',
    delivered: 'EMAIL_DELIVERED',
    opened: 'EMAIL_OPENED',
    paid: 'PAID',
    salaryCreated: 'SALARY_CREATED',
    salaryPaid: 'SALARY_PAID',
    signed: 'DOC_SIGNED',
};

const DESC_ON_KEY: Record<TimelineStepType, string> = {
    sent: 'EMAIL_SENT_DESC',
    delivered: 'EMAIL_DELIVERED_DESC',
    opened: 'EMAIL_OPENED_DESC',
    paid: 'EMAIL_PAID_DESC',
    salaryCreated: 'EMAIL_SALARY_CREATED_DESC',
    salaryPaid: 'EMAIL_SALARY_PAID_DESC',
    signed: 'DOC_SIGNED_DESC',
};

const DESC_OFF_KEY: Record<TimelineStepType, string> = {
    sent: 'EMAIL_SENT_PENDING',
    delivered: 'EMAIL_DELIVERED_PENDING',
    opened: 'EMAIL_OPENED_PENDING',
    paid: 'EMAIL_PAYMENT_PENDING',
    salaryCreated: 'EMAIL_SALARY_NOT_CREATED',
    salaryPaid: 'EMAIL_SALARY_PAYMENT_PENDING',
    signed: 'DOC_SIGNED_PENDING',
};

const DOC_OUTCOME_LABEL_KEY: Record<'declined' | 'cancelled' | 'expired', string> = {
    declined: 'DOC_DECLINED',
    cancelled: 'DOC_CANCELLED',
    expired: 'DOC_EXPIRED',
};

const DOC_OUTCOME_DESC_KEY_BY_KIND: Record<'customer-offer' | 'customer-contract', Record<'declined' | 'cancelled' | 'expired', string>> = {
    'customer-offer': {
        declined: 'DOC_OFFER_DECLINED_DESC',
        cancelled: 'DOC_OFFER_CANCELLED_DESC',
        expired: 'DOC_OFFER_EXPIRED_DESC',
    },
    'customer-contract': {
        declined: 'DOC_CONTRACT_DECLINED_DESC',
        cancelled: 'DOC_CONTRACT_CANCELLED_DESC',
        expired: 'DOC_CONTRACT_EXPIRED_DESC',
    },
};

const formatDate = (iso?: string | null): string =>
    iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—';

const formatTime = (iso?: string | null): string =>
    iso ? new Date(iso).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' }) : '';

const formatDateTime = (iso?: string | null): string =>
    iso ? `${formatDate(iso)} ${formatTime(iso)}` : '—';

const EmailTrackingModal: React.FC<EmailTrackingModalProps> = ({
    show,
    invoiceId,
    variant = 'invoice',
    kind = 'invoice',
    invoiceStatus,
    isSalaryCreated,
    invoiceSalaryStatus,
    documentOutcome = 'pending',
    documentSentDate,
    documentNumber,
    titleKey,
    onClose,
}) => {
    const { t } = useTranslation();
    const { details, loading, error, loadTracking, clear } = useEmailTracking();
    const modalRef = useRef<HTMLDivElement | null>(null);
    const [showScrollHint, setShowScrollHint] = useState(false);
    const [showScrollUpHint, setShowScrollUpHint] = useState(false);

    // Load on open; clear on close so a stale timeline never flashes next time.
    useEffect(() => {
        if (show && invoiceId != null) {
            loadTracking(invoiceId, kind);
        }
        if (!show) {
            clear();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, invoiceId, kind]);

    // Close on Escape.
    useEffect(() => {
        if (!show) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [show, onClose]);

    // Show "scroll down"/"scroll up" hints whenever the modal is taller than
    // its viewport and there's unscrolled content in that direction — each
    // re-checked on scroll, whenever content finishes loading (its height
    // changes), and on window resize.
    const checkScrollable = () => {
        const el = modalRef.current;
        if (!el) return;
        setShowScrollHint(el.scrollHeight - el.clientHeight - el.scrollTop > 24);
        setShowScrollUpHint(el.scrollTop > 24);
    };

    useEffect(() => {
        checkScrollable();
        window.addEventListener('resize', checkScrollable);
        return () => window.removeEventListener('resize', checkScrollable);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, loading, details]);

    const scrollToBottom = () => {
        modalRef.current?.scrollTo({ top: modalRef.current.scrollHeight, behavior: 'smooth' });
    };

    const scrollToTop = () => {
        modalRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (!show) return null;

    const status: EmailStatus = details?.status ?? 'not_sent';
    const events = details?.events ?? [];

    const isDocument = variant === 'document';
    const stepOrder = STEP_ORDER_BY_VARIANT[variant];

    // Each node reflects only its own actual flag/event — express payment can
    // create (and pay) a salary without the invoice ever being marked paid or
    // the email ever being opened, so no stage is forced complete by a later one.
    const salaryPaidDone = invoiceSalaryStatus === SALARY_PAID_STATUS;
    const salaryCreatedDone = !!isSalaryCreated;
    const invoicePaidDone = invoiceStatus === INVOICE_PAID_STATUS;
    const documentTerminated = documentOutcome !== 'pending';
    const documentSentDone = !!documentSentDate;

    const emailReached = EMAIL_REACHED[status] ?? 0;

    const failIndex = isDocument
        ? (status === 'bounced' || status === 'failed' ? 1 : -1)
        : status === 'failed' ? 0 : status === 'bounced' ? 1 : -1;
    const failKind = failIndex < 0 ? '' : status === 'bounced' ? 'bounce' : 'fail';

    const doneAt = (i: number): boolean => {
        if (isDocument) {
            if (i === 0) return documentSentDone;
            if (i < EMAIL_STEP_COUNT) return i < emailReached;
            return documentTerminated;
        }
        if (i < EMAIL_STEP_COUNT) return i < emailReached;
        return i === 3 ? invoicePaidDone : i === 4 ? salaryCreatedDone : salaryPaidDone;
    };

    let lastDoneIndex = -1;
    stepOrder.forEach((_, i) => {
        if (doneAt(i)) lastDoneIndex = i;
    });

    const timelineStatus: TimelineStatus = isDocument
        ? documentOutcome !== 'pending'
            ? documentOutcome
            : status === 'not_sent' && documentSentDone
                ? 'sent'
                : status
        : salaryPaidDone
            ? 'salary_paid'
            : salaryCreatedDone
                ? 'salary_created'
                : invoicePaidDone
                    ? 'paid'
                    : status;

    // Portal to document.body — the modal mounts inside the table tree, whose
    // ancestor panel carries a `transform` that would otherwise trap a
    // position:fixed overlay. (Same reason as RequestCreditInvoiceModal.)
    return ReactDOM.createPortal(
        <div className="email-tracking-modal-overlay" role="presentation" onClick={onClose}>
            {/* Non-scrolling shell — lets the scroll-down hint float over the
                modal's own scrolling content instead of scrolling away with it. */}
            <div className="email-tracking-modal-shell" onClick={(e) => e.stopPropagation()}>
            <div
                className="email-tracking-modal"
                ref={modalRef}
                onScroll={checkScrollable}
                role="dialog"
                aria-modal="true"
                aria-labelledby="email-tracking-modal-title"
            >
                {/* Header */}
                <div className="email-tracking-modal__header">
                    <div className="email-tracking-modal__header-row1">
                        <h2 className="email-tracking-modal__title" id="email-tracking-modal-title">
                            {t(titleKey ?? 'INVOICE_TRACKING')}
                        </h2>
                        <button
                            type="button"
                            className="email-tracking-modal__close"
                            onClick={onClose}
                            aria-label={t('CLOSE')}
                        >
                            <CloseIcon />
                        </button>
                    </div>

                    {!loading && !error && details && (
                        <div className="email-tracking-modal__header-row2">
                            <div className="email-tracking-modal__status-block">
                                <div className="email-tracking-modal__status-icon">
                                    <span className="email-tracking-modal__status-icon-glyph">
                                        <TimelineStatusIcon status={timelineStatus} size={30} />
                                    </span>
                                </div>
                                <div className="email-tracking-modal__status-text">
                                    <span className="email-tracking-modal__status-label">{t('CURRENT_STATUS')}</span>
                                    <span className="email-tracking-modal__status-value">
                                        {t(mapTimelineStatus(timelineStatus))}
                                    </span>
                                    <span className="email-tracking-modal__status-invoice">
                                        {isDocument
                                            ? documentNumber
                                            : `${t('INVOICE')} #${details.invoiceNumber}`}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Body */}
                <div className="email-tracking-modal__body">
                    {loading && (
                        <div className="email-tracking-modal__state">
                            <span className="email-tracking-modal__spinner" aria-hidden="true" />
                            <span>{t('LOADING')}</span>
                        </div>
                    )}

                    {!loading && error && (
                        <div className="email-tracking-modal__state email-tracking-modal__state--error">
                            {t('EMAIL_TRACKING_LOAD_ERROR')}
                        </div>
                    )}

                    {!loading && !error && details && (
                        <div className="email-tracking-modal__timeline">
                            {stepOrder.map((type, i) => {
                                const ev = i < EMAIL_STEP_COUNT ? events[i] : undefined;
                                const ts = isDocument && i === 0
                                    ? documentSentDate ?? null
                                    : ev?.timestamp ?? null;
                                const done = doneAt(i);
                                const isFail = i === failIndex;
                                const isLast = i === stepOrder.length - 1;
                                const isNegativeOutcomeNode =
                                    isDocument && isLast && documentOutcome !== 'signed' && documentOutcome !== 'pending';
                                const state = isFail
                                    ? 'fail'
                                    : isNegativeOutcomeNode
                                        ? documentOutcome
                                        : done
                                            ? failIndex < 0 && i === lastDoneIndex
                                                ? 'active'
                                                : 'done'
                                            : 'pending';
                                const hasTransit = state === 'active' && !isLast;
                                const time = isFail
                                    ? formatDateTime(details.sentOn)
                                    : ts
                                        ? formatDateTime(ts)
                                        : '—';
                                const negativeOutcome = documentOutcome as 'declined' | 'cancelled' | 'expired';
                                const labelKey = isNegativeOutcomeNode
                                    ? DOC_OUTCOME_LABEL_KEY[negativeOutcome]
                                    : STEP_LABEL_KEY[type];
                                const descKey = isFail
                                    ? failKind === 'bounce'
                                        ? 'EMAIL_BOUNCED_DESC'
                                        : 'EMAIL_FAILED_DESC'
                                    : isNegativeOutcomeNode
                                        ? DOC_OUTCOME_DESC_KEY_BY_KIND[kind as 'customer-offer' | 'customer-contract'][negativeOutcome]
                                        : done
                                            ? DESC_ON_KEY[type]
                                            : DESC_OFF_KEY[type];
                                const rowCls = [
                                    'email-tracking-modal__row',
                                    `email-tracking-modal__row--${state}`,
                                    isFail && failKind === 'bounce' ? 'email-tracking-modal__row--bounce' : '',
                                    hasTransit ? 'email-tracking-modal__row--transit' : '',
                                ].filter(Boolean).join(' ');
                                return (
                                    <div
                                        className={rowCls}
                                        key={type}
                                        style={{ ['--i' as string]: i } as React.CSSProperties}
                                    >
                                        <div className="email-tracking-modal__row-icon">
                                            {isNegativeOutcomeNode
                                                ? <FailedIcon size={state === 'pending' ? 16 : 20} />
                                                : <StepIcon type={type} size={state === 'pending' ? 16 : 20} />}
                                        </div>
                                        <div className="email-tracking-modal__row-body">
                                            <div className="email-tracking-modal__row-top">
                                                <span className="email-tracking-modal__row-title">
                                                    {t(labelKey)}
                                                </span>
                                                <span className="email-tracking-modal__row-time">{time}</span>
                                            </div>
                                            <div className="email-tracking-modal__row-desc">{t(descKey)}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="email-tracking-modal__footer">
                    <button type="button" className="email-tracking-modal__btn" onClick={onClose}>
                        {t('CLOSE')}
                    </button>
                </div>
            </div>

            {showScrollUpHint && (
                <button
                    type="button"
                    className="email-tracking-modal__scroll-hint email-tracking-modal__scroll-hint--up"
                    onClick={scrollToTop}
                    aria-label={t('SCROLL_UP')}
                >
                    <ChevronUpIcon size={16} />
                </button>
            )}
            {showScrollHint && (
                <button
                    type="button"
                    className="email-tracking-modal__scroll-hint email-tracking-modal__scroll-hint--down"
                    onClick={scrollToBottom}
                    aria-label={t('SCROLL_DOWN')}
                >
                    <ChevronDownIcon size={16} />
                </button>
            )}
            </div>
        </div>,
        document.body
    );
};

export default EmailTrackingModal;
